// @ts-nocheck
import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Mail, Send, MessageCircle, Info } from "lucide-react";

export function IntegrationsTab() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  // SMTP State
  const [smtpHabilitado, setSmtpHabilitado] = useState(false);
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUsuario, setSmtpUsuario] = useState("");
  const [smtpSenha, setSmtpSenha] = useState("");
  const [smtpRemetentNome, setSmtpRemetenteNome] = useState("");
  const [smtpRemetenteEmail, setSmtpRemetenteEmail] = useState("");
  const [usaTLS, setUsaTLS] = useState(true);

  // WhatsApp State
  const [whatsappHabilitado, setWhatsappHabilitado] = useState(false);
  const [whatsappProvider, setWhatsappProvider] = useState("");
  const [whatsappApiUrl, setWhatsappApiUrl] = useState("");
  const [whatsappNumero, setWhatsappNumero] = useState("");
  const [whatsappToken, setWhatsappToken] = useState("");

  useEffect(() => {
    carregarConfiguracoes();
  }, []);

  const carregarConfiguracoes = async () => {
    try {
      const { data, error } = await supabase
        .from("configuracoes_integracao")
        .select("*")
        .single() as any;

      if (error) throw error;

      if (data) {
        setSmtpHabilitado(data.smtp_habilitado ?? false);
        setSmtpHost(data.smtp_host ?? "");
        setSmtpPort(data.smtp_port ?? 587);
        setSmtpUsuario(data.smtp_usuario ?? "");
        setSmtpRemetenteNome(data.smtp_remetente_nome ?? "");
        setSmtpRemetenteEmail(data.smtp_remetente_email ?? "");
        setUsaTLS(data.smtp_usa_tls ?? true);

        setWhatsappHabilitado(data.whatsapp_habilitado ?? false);
        setWhatsappProvider(data.whatsapp_provider ?? "");
        setWhatsappApiUrl(data.whatsapp_api_url ?? "");
        setWhatsappNumero(data.whatsapp_numero_origem ?? "");
      }
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
    }
  };

  const salvarConfiguracoes = async () => {
    setIsLoading(true);
    try {
      // Validações
      if (smtpHabilitado) {
        if (!smtpHost || !smtpUsuario || !smtpSenha) {
          toast({
            title: "Erro",
            description: "Preencha todos os campos obrigatórios do SMTP",
            variant: "destructive",
          });
          return;
        }
      }

      if (whatsappHabilitado) {
        if (!whatsappProvider || !whatsappApiUrl || !whatsappNumero || !whatsappToken) {
          toast({
            title: "Erro",
            description: "Preencha todos os campos obrigatórios do WhatsApp",
            variant: "destructive",
          });
          return;
        }
      }

      // Atualizar configurações não-sensíveis
      const { error } = await supabase
        .from("configuracoes_integracao")
        .upsert({
          id: "00000000-0000-0000-0000-000000000001",
          smtp_habilitado: smtpHabilitado,
          smtp_host: smtpHost,
          smtp_port: smtpPort,
          smtp_usuario: smtpUsuario,
          smtp_usa_tls: usaTLS,
          smtp_remetente_nome: smtpRemetentNome,
          smtp_remetente_email: smtpRemetenteEmail,
          whatsapp_habilitado: whatsappHabilitado,
          whatsapp_provider: whatsappProvider,
          whatsapp_api_url: whatsappApiUrl,
          whatsapp_numero_origem: whatsappNumero,
          updated_at: new Date().toISOString(),
        } as any) as any;

      if (error) throw error;

      // Salvar secrets (senha SMTP e token WhatsApp)
      if (smtpSenha || whatsappToken) {
        const { data: { session } } = await supabase.auth.getSession();
        
        const response = await supabase.functions.invoke('save-integration-secrets', {
          body: {
            smtp_password: smtpSenha || null,
            whatsapp_token: whatsappToken || null,
          },
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        });

        if (response.error) {
          console.error("Erro ao salvar secrets:", response.error);
        }
      }

      toast({
        title: "✅ Configurações salvas",
        description: "As integrações foram atualizadas com sucesso",
      });

      // Limpar campos de senha
      setSmtpSenha("");
      setWhatsappToken("");
    } catch (error) {
      console.error("Erro ao salvar configurações:", error);
      toast({
        title: "Erro",
        description: "Não foi possível salvar as configurações",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getProviderDocs = () => {
    const docs = {
      twilio: "https://www.twilio.com/docs/whatsapp",
      "evolution-api": "https://doc.evolution-api.com/",
      wppconnect: "https://wppconnect.io/",
      "meta-api": "https://developers.facebook.com/docs/whatsapp",
    };
    return docs[whatsappProvider as keyof typeof docs];
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>📡 Integrações</CardTitle>
        <CardDescription>
          Configure serviços de e-mail e WhatsApp para notificações automáticas
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* SEÇÃO SMTP */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">📧 Configuração de E-mail (SMTP)</h3>
              <p className="text-sm text-muted-foreground">
                Envie e-mails automáticos (relatórios, notificações, confirmações)
              </p>
            </div>
            <Switch
              checked={smtpHabilitado}
              onCheckedChange={setSmtpHabilitado}
              aria-label="Habilitar SMTP"
            />
          </div>

          {smtpHabilitado && (
            <div className="ml-4 pl-4 border-l-2 border-primary space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Servidor SMTP (Host) *</Label>
                  <Input
                    placeholder="smtp.gmail.com"
                    value={smtpHost}
                    onChange={(e) => setSmtpHost(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Exemplos: smtp.gmail.com, smtp.office365.com, smtp.zoho.com
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Porta *</Label>
                  <Input
                    type="number"
                    placeholder="587"
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(parseInt(e.target.value))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Geralmente 587 (TLS) ou 465 (SSL)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Usuário / E-mail *</Label>
                  <Input
                    type="email"
                    placeholder="seu-email@dominio.com"
                    value={smtpUsuario}
                    onChange={(e) => setSmtpUsuario(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Senha *</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={smtpSenha}
                    onChange={(e) => setSmtpSenha(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    🔒 Armazenada de forma segura e criptografada
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Nome do Remetente</Label>
                  <Input
                    placeholder="Confeitaria Pudinzim"
                    value={smtpRemetentNome}
                    onChange={(e) => setSmtpRemetenteNome(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>E-mail do Remetente</Label>
                  <Input
                    type="email"
                    placeholder="contato@pudinzim.com"
                    value={smtpRemetenteEmail}
                    onChange={(e) => setSmtpRemetenteEmail(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={usaTLS} onCheckedChange={setUsaTLS} />
                <Label>Usar TLS/SSL (recomendado)</Label>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  As credenciais SMTP serão armazenadas com segurança. Para Gmail, use uma senha de app específica.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>

        <Separator />

        {/* SEÇÃO WHATSAPP */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">💬 Configuração de WhatsApp API</h3>
              <p className="text-sm text-muted-foreground">
                Envie mensagens automáticas via WhatsApp (notificações, lembretes)
              </p>
            </div>
            <Switch
              checked={whatsappHabilitado}
              onCheckedChange={setWhatsappHabilitado}
              aria-label="Habilitar WhatsApp"
            />
          </div>

          {whatsappHabilitado && (
            <div className="ml-4 pl-4 border-l-2 border-green-500 space-y-4">
              <div className="space-y-2">
                <Label>Provedor da API *</Label>
                <Select value={whatsappProvider} onValueChange={setWhatsappProvider}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o provedor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="twilio">Twilio</SelectItem>
                    <SelectItem value="evolution-api">Evolution API</SelectItem>
                    <SelectItem value="wppconnect">WPPConnect</SelectItem>
                    <SelectItem value="meta-api">Meta (Facebook) API</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Escolha o serviço que você está usando para enviar mensagens
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>URL da API *</Label>
                  <Input
                    placeholder="https://api.seuservico.com"
                    value={whatsappApiUrl}
                    onChange={(e) => setWhatsappApiUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Endereço base da API do WhatsApp
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Número de Origem (WhatsApp) *</Label>
                  <Input
                    placeholder="5515999887766"
                    value={whatsappNumero}
                    onChange={(e) => setWhatsappNumero(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Número com código do país (ex: 5515999887766)
                  </p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label>Token de Autenticação (API Key) *</Label>
                  <Input
                    type="password"
                    placeholder="••••••••••••••••••••"
                    value={whatsappToken}
                    onChange={(e) => setWhatsappToken(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    🔒 Token fornecido pelo provedor do WhatsApp
                  </p>
                </div>
              </div>

              {whatsappProvider && getProviderDocs() && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>Documentação do Provedor</AlertTitle>
                  <AlertDescription>
                    <a
                      href={getProviderDocs()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline"
                    >
                      📖 {whatsappProvider} - Documentação oficial
                    </a>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </div>
      </CardContent>

      <CardFooter className="flex justify-end">
        <Button onClick={salvarConfiguracoes} disabled={isLoading}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Salvar Configurações
        </Button>
      </CardFooter>
    </Card>
  );
}
